import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Avatar, CircularProgress } from '@mui/material';

interface ProfilePhotoGalleryProps {
  pictures: string[];
  name: string;
}

const loadImage = async (src?: string): Promise<boolean> => {
  if (!src) return false;
  try {
    const response = await fetch(src, { method: 'HEAD' });
    return response.ok;
  } catch (err) {
    console.error(`Error loading picture: ${err}`);
    return false;
  }
};

const usePictures = (pictures: string[]) => {
  const [loading, setLoading] = useState(Boolean(pictures?.length));
  const [validPictures, setValidPictures] = useState<string[]>([]);

  useEffect(() => {
    const loadPictures = async () => {
      const validPicturesList: string[] = [];
      setLoading(true);
      await Promise.all(pictures.map(async (picture, index) => {
        if (await loadImage(picture)) {
          validPicturesList[index] = picture;
        }
      }));
      setValidPictures(validPicturesList.filter(Boolean));
      setLoading(false);
    };

    if (pictures?.length) {
      loadPictures();
    } else {
      setValidPictures([]);
      setLoading(false);
    }
  }, [pictures]);

  return { pictures: validPictures, loading };
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export default function ProfilePhotoGallery({ pictures, name }: ProfilePhotoGalleryProps) {
  const { pictures: validPictures, loading } = usePictures(pictures);
  const [active, setActive] = useState(0);
  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setActive(0);
  }, [pictures]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!ref.current || validPictures.length <= 1) return;
    
    const clickableImage = ref.current;
    const imageRect = clickableImage.getBoundingClientRect();
    const clickX = e.clientX - imageRect.left;
    const imageWidth = clickableImage.width;
    const clickPosition = clickX / imageWidth;

    if (clickPosition < 0.5) {
      setActive(i => {
        if (i <= 0) {
          clickableImage.classList.remove('bounce-right');
          clickableImage.classList.add('bounce-right');
          clickableImage.addEventListener("animationend", function () {
            clickableImage.classList.remove("bounce-right");
          });
          return Math.max(0, i);
        }
        return i - 1;
      });
    } else {
      setActive(i => {
        if (i >= validPictures.length - 1) {
          clickableImage.classList.remove('bounce-left');
          clickableImage.classList.add('bounce-left');
          clickableImage.addEventListener("animationend", function () {
            clickableImage.classList.remove("bounce-left");
          });
          return Math.min(i, validPictures.length - 1);
        }
        return i + 1;
      });
    }
  }, [validPictures]);

  // Desktop/Tablet: Show multiple photos horizontally
  const renderDesktopGallery = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      gap: 2,
      width: '100vw',
      maxWidth: 'none',
      margin: '0 auto',
      padding: '0 16px',
      boxSizing: 'border-box',
      marginLeft: 'calc(-50vw + 50%)',
      marginRight: 'calc(-50vw + 50%)'
    }}>
      {validPictures.map((picture, index) => (
        <Box
          key={index}
          sx={{ 
            position: 'relative',
            width: 'calc((100vw - 32px - 16px) / 3)',
            maxWidth: '400px',
            margin: 0,
            flexShrink: 0
          }}
        >
          <Box sx={{
            height: 0,
            width: '100%',
            paddingBottom: '125%', // 4:5 aspect ratio to match admin presentation
            position: 'relative',
            borderRadius: 3,
            overflow: 'hidden',
            backgroundColor: '#38191E',
            backgroundImage: picture ? `url(${picture})` : 'none',
            backgroundSize: '101% 101%', // Slightly larger to prevent edge artifacts
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }} >
            {picture ? (
              <img 
                src={picture} 
                alt={`${name} ${index + 1}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  opacity: 0,
                  pointerEvents: 'none'
                }}
              />
            ) : (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: '100%',
                margin: 0,
                borderRadius: '12px',
                background: 'rgba(255,255,255,0.2)',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                fontSize: '3rem',
                fontWeight: 'bold',
                color: 'white'
              }}>
                {getInitials(name)}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  );

  // Mobile: Show slideshow
  const renderMobileSlideshow = () => (
    <Box sx={{
      position: 'relative',
      width: '100%',
      maxWidth: '400px',
      margin: '0 auto',
      '& img.bounce-left': {
        animation: 'bounceLeft 100ms ease-in-out forwards',
      },
      '& img.bounce-right': {
        animation: 'bounceRight 100ms ease-in-out forwards',
      },
    }}>
      <Box sx={{
        height: 0,
        width: '100%',
        paddingBottom: '125%', // 4:5 aspect ratio to match admin presentation
        position: 'relative',
        borderRadius: 3,
        overflow: 'hidden',
        backgroundColor: '#38191E',
        backgroundImage: validPictures[active] ? `url(${validPictures[active]})` : 'none',
        backgroundSize: '101% 101%', // Slightly larger to prevent edge artifacts
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        cursor: validPictures.length > 1 ? 'pointer' : 'default',
      }}
      onClick={handleClick}
      >
        {validPictures[active] ? (
          <img
            ref={ref}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              pointerEvents: 'none'
            }}
            src={validPictures[active]}
            alt={`${name} ${active + 1}`}
          />
        ) : null}
      </Box>
      
      {validPictures.length > 1 && (
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          background: "linear-gradient(to top, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0) 100%)",
          height: 32,
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          alignItems: 'flex-end',
          padding: '0 2px 4px',
          boxSizing: 'border-box',
        }}>
          {validPictures.map((_, i) => (
            <Box 
              key={i} 
              sx={{
                flex: 1,
                height: 4,
                margin: '0 2px',
                borderRadius: '2px',
                backgroundColor: 'white',
                opacity: active === i ? 1 : 0.5
              }} 
            />
          ))}
        </Box>
      )}
      
      {validPictures.length === 0 && !loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          margin: 0,
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.2)',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          border: '3px solid rgba(255,255,255,0.3)',
        }}>
          <Avatar
            sx={{ 
              width: 100, 
              height: 140, 
              bgcolor: 'rgba(255,255,255,0.2)',
              fontSize: '2rem',
              fontWeight: 'bold',
              border: '3px solid rgba(255,255,255,0.3)',
              borderRadius: '12px'
            }}
          >
            {getInitials(name)}
          </Avatar>
        </Box>
      )}
      
      {loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
          width: '100%',
          margin: 0,
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.2)',
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
        }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ mb: 4 }}>
      {/* Desktop/Tablet */}
      <Box sx={{ 
        display: { xs: 'none', sm: 'block' }
      }}>
        {renderDesktopGallery()}
      </Box>
      
      {/* Mobile */}
      <Box sx={{ 
        display: { xs: 'block', sm: 'none' }
      }}>
        {renderMobileSlideshow()}
      </Box>
    </Box>
  );
} 
